
import React, { useRef, useEffect, RefObject } from 'react';
import { X } from 'lucide-react';
import SearchInput from './SearchInput';
import SearchSuggestions from './SearchSuggestions';
import SearchTrekResults from './SearchTrekResults';
import PopularSearches from './PopularSearches';
import { Trek } from '@/lib/types/trek-types';
import { Loader } from '@/components/ui/loader';

interface ExpandedSearchOverlayProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSearchSubmit: () => void;
  onClearSearch: () => void;
  onClose: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  inputRef: RefObject<HTMLInputElement>;
  isLoading: boolean;
  isLoadingPopular: boolean;
  suggestions: string[];
  trekResults: Trek[];
  popularSearches: string[];
  highlightedIndex: number;
  onSuggestionClick: (suggestion: string) => void;
  onTrekResultClick: (trekId: string) => void;
  onPopularSearchClick: (term: string) => void;
  onMouseEnterSuggestion: (index: number) => void;
  isMobile: boolean;
}

const ExpandedSearchOverlay: React.FC<ExpandedSearchOverlayProps> = ({
  searchQuery,
  onSearchChange,
  onSearchSubmit,
  onClearSearch,
  onClose,
  onKeyDown,
  inputRef,
  isLoading,
  isLoadingPopular,
  suggestions,
  trekResults,
  popularSearches,
  highlightedIndex,
  onSuggestionClick,
  onTrekResultClick,
  onPopularSearchClick,
  onMouseEnterSuggestion,
  isMobile
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Focus the input when overlay opens
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);

    // Add escape key listener
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [inputRef, onClose]);

  // Handle backdrop click to close the overlay
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Check if the click was on the backdrop and not on the content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getTotalResults = () => suggestions.length + trekResults.length;

  return (
    <div 
      className="fixed inset-0 z-50 flex flex-col items-center pt-16 bg-background/80 backdrop-blur-sm animate-fade-in"
      onClick={handleBackdropClick}
      ref={overlayRef}
    >
      <div className="absolute right-4 top-4">
        <button 
          onClick={onClose}
          className="p-2 rounded-full hover:bg-muted transition-colors"
          aria-label="Close search"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <div 
        ref={contentRef} 
        className="w-full max-w-3xl px-4 animate-fade-up" 
        style={{ animationDelay: '50ms' }}
      >
        <div className="mb-6">
          <SearchInput 
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            onSearchSubmit={onSearchSubmit}
            onClearSearch={onClearSearch}
            onFocus={() => {}}
            onKeyDown={onKeyDown}
            inputRef={inputRef}
            isMobile={isMobile}
          />
        </div>

        <div className="bg-background border border-border rounded-xl shadow-lg overflow-hidden">
          {isLoading ? (
            <div className="p-8 flex justify-center">
              <Loader text="Searching..." />
            </div>
          ) : (
            <>
              {suggestions.length > 0 && (
                <div className="animate-fade-in" style={{ animationDelay: '100ms' }}>
                  <SearchSuggestions 
                    suggestions={suggestions}
                    searchQuery={searchQuery}
                    highlightedIndex={highlightedIndex}
                    onSuggestionClick={onSuggestionClick}
                    onMouseEnter={onMouseEnterSuggestion}
                  />
                </div>
              )}
              
              {trekResults.length > 0 && (
                <div className="animate-fade-in" style={{ animationDelay: '150ms' }}>
                  <SearchTrekResults 
                    results={trekResults}
                    searchQuery={searchQuery}
                    highlightedIndex={highlightedIndex}
                    startIndex={suggestions.length}
                    onResultClick={onTrekResultClick}
                    onMouseEnter={onMouseEnterSuggestion}
                  />
                </div>
              )}
              
              {suggestions.length === 0 && trekResults.length === 0 && searchQuery.trim().length > 1 && (
                <div className="p-8 text-center text-muted-foreground animate-fade-in">
                  No results found for "{searchQuery}"
                </div>
              )}
              
              {(suggestions.length > 0 || trekResults.length > 0) && searchQuery.trim().length > 1 && (
                <div className="p-4 border-t border-border animate-fade-in" style={{ animationDelay: '200ms' }}>
                  <button
                    onClick={onSearchSubmit}
                    className="w-full text-center py-3 text-sm font-medium text-primary hover:underline hover:bg-muted/50 rounded-md transition-colors"
                  >
                    View all results for "{searchQuery}"
                  </button>
                </div>
              )}

              {searchQuery.trim().length <= 1 && (
                <div className="p-6 animate-fade-in" style={{ animationDelay: '100ms' }}>
                  <h3 className="text-lg font-medium mb-4">Popular Searches</h3>
                  <div className="flex flex-wrap gap-2">
                    {isLoadingPopular ? (
                      <div className="w-full flex justify-center py-4">
                        <Loader size="sm" text="Loading popular searches..." />
                      </div>
                    ) : (
                      popularSearches.map((term, i) => (
                        <button
                          key={`popular-overlay-${i}`}
                          onClick={() => onPopularSearchClick(term)}
                          className="px-4 py-2 bg-muted/60 hover:bg-muted rounded-full text-sm font-medium transition-colors"
                        >
                          {term}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpandedSearchOverlay;
